import {Box, Heading, HStack, Show, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {isNotNil} from 'ramda-adjunct';

import {AuctionVehicleDetailResponseBody} from '@dms/api/core';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, suffixTestId, TestIdProps} from 'shared/web';

import {calculatePriceExclVat} from '../utils/calculatePriceExclVat';

interface AuctionCurrentBidProps extends TestIdProps {
  auction: AuctionVehicleDetailResponseBody;
}

export function AuctionCurrentBid(props: AuctionCurrentBidProps) {
  const formatCurrency = useFormatCurrency();
  const auction = props.auction;
  const auctionVehicle = auction.auctionVehicle;
  const showConverted =
    auctionVehicle.currency !== auctionVehicle.originalCurrency &&
    isNotNil(auctionVehicle.originalPriceActual);
  const priceExclVat = calculatePriceExclVat(
    auctionVehicle.originalPriceActual ?? null,
    auctionVehicle.vatRate
  );

  return (
    <Box backgroundColor="palettes.neutral.20.100" padding={4}>
      <VStack align="flex-end" spacing={1}>
        <Text size="xSmall" color="secondary" data-testid={suffixTestId('currentBidLabel', props)}>
          {i18n.t('entity.auction.labels.currentBid')}
        </Text>
        <HStack spacing={1} align="baseline">
          <Heading size={2} alternative data-testid={suffixTestId('currentBidPrice', props)}>
            {isNotNil(auctionVehicle.priceActual)
              ? (formatCurrency(auctionVehicle.priceActual, auctionVehicle.currency ?? '') ??
                EMPTY_PLACEHOLDER)
              : EMPTY_PLACEHOLDER}
          </Heading>
          <Show when={showConverted}>
            <Heading
              size={2}
              color="tertiary"
              alternative
              data-testid={suffixTestId('convertedPrice', props)}
            >
              {`/ ${formatCurrency(auctionVehicle.originalPriceActual ?? 0, auctionVehicle.originalCurrency ?? '') ?? EMPTY_PLACEHOLDER}`}
            </Heading>
          </Show>
        </HStack>
        <Show when={isNotNil(priceExclVat)}>
          <Text size="xSmall" color="secondary" data-testid={suffixTestId('priceExclVat', props)}>
            {formatCurrency(priceExclVat ?? 0, auctionVehicle.originalCurrency ?? '') ??
              EMPTY_PLACEHOLDER}{' '}
            {i18n.t('general.labels.withoutVat')}
          </Text>
        </Show>
      </VStack>
    </Box>
  );
}
